"use client";

import { useState } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import { INDUSTRY_PRESETS } from "@/data/industryPresets";
import { startHref, trackMarketing } from "@/components/marketing/launch";

import styles from "./HomeUspBlock.module.scss";

const cx = cnBind.bind(styles);

const SCENARIO_ASSETS = [
    {
        id: "order" as const,
        clientImage: INDUSTRY_PRESETS.retail.slides[4]?.image || "/images/presets/ecom-checkout.png",
        crmImage: "/images/presets/ecom-crm-order-card.png",
    },
    {
        id: "booking" as const,
        clientImage: "/images/presets/hero-line-studio-miniapp.png",
        crmImage: "/images/presets/hero-line-studio-crm.png",
    },
    {
        id: "lead" as const,
        clientImage: INDUSTRY_PRESETS.services.slides[0]?.image,
        crmImage: INDUSTRY_PRESETS.services.crmSlides?.[0]?.image,
    },
];

/** Связка клиент → команда: три коротких сценария без каталога модулей. */
export const HomeUspBlock = () => {
    const { t } = useTranslation("common");
    const [active, setActive] = useState(0);
    const asset = SCENARIO_ASSETS[active] ?? SCENARIO_ASSETS[0];
    const copy = t(`home.usp.scenarios.${asset.id}`, { returnObjects: true }) as {
        tab: string;
        title: string;
        text: string;
        caption: string;
    };

    return (
        <section className={cx("section")} id="how-it-works" aria-labelledby="home-usp-title">
            <div className={cx("inner", "container")}>
                <p className={cx("eyebrow")}>{t("home.usp.eyebrow")}</p>
                <h2 id="home-usp-title" className={cx("title")}>
                    {t("home.usp.title")}
                </h2>
                <p className={cx("lead")}>{t("home.usp.lead")}</p>

                <div className={cx("tabs")} role="tablist" aria-label={t("home.usp.tabsAriaLabel")}>
                    {SCENARIO_ASSETS.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={index === active}
                            className={cx("tab", index === active && "tabOn")}
                            onClick={() => setActive(index)}
                        >
                            {t(`home.usp.scenarios.${item.id}.tab`)}
                        </button>
                    ))}
                </div>

                <article className={cx("scenario")} aria-live="polite">
                    <div className={cx("scenarioCopy")}>
                        <h3>{copy.title}</h3>
                        <p>{copy.text}</p>
                        <a
                            className={cx("start")}
                            href={startHref()}
                            onClick={() =>
                                trackMarketing("home_usp_cta", {
                                    source: "how_it_works",
                                    scenario: asset.id,
                                })
                            }
                        >
                            {t("home.usp.cta")}
                        </a>
                    </div>
                    <div className={cx("scenarioVisual")}>
                        {asset.clientImage ? (
                            <figure className={cx("shot", "shotPhone")}>
                                <img
                                    src={asset.clientImage}
                                    alt=""
                                    width={460}
                                    height={1024}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <figcaption>{t("home.usp.figcaptionClient")}</figcaption>
                            </figure>
                        ) : null}
                        {asset.crmImage ? (
                            <figure className={cx("shot", "shotCrm")}>
                                <img
                                    src={asset.crmImage}
                                    alt=""
                                    width={1024}
                                    height={640}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <figcaption>{t("home.usp.figcaptionCrm")}</figcaption>
                            </figure>
                        ) : null}
                        <p className={cx("sceneCaption")}>{copy.caption}</p>
                    </div>
                </article>
            </div>
        </section>
    );
};
